/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import _union from "lodash/union";
import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  TextField,
} from "@fluentui/react";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { spinnerStyles } from "Components/Common/SharedListStyles.styles";
import { OperationStatus } from "Constants/OperationStatus";
import {
  getNetworkOperationStatusAddMember,
  getNetworkOperationStatusFetchMicrosoftMembers,
  getNetworkOperationStatusRemoveMember,
  getSelectedDistributionListMicrosoftMembers,
} from "Dataflows/DistributionLists/DistributionListsSelector";
import { IDistributionGroupMember } from "Interfaces/DistributionLists/IDistributionGroupMember";

import { IMicrosoftMembersListProps } from "./IMicrosoftMembersListProps";
import { detailListStyles } from "./MicrosoftMembersList.styles";

/**
 * The Microsoft Members List Component.
 * @method
 * @returns {ReactElement} The Microsoft Members List component
 */
const MicrosoftMembersList = (props: IMicrosoftMembersListProps) => {
  const microsoftMembers = useSelector(getSelectedDistributionListMicrosoftMembers);

  const [members, setMembers] = useState(microsoftMembers);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);

  const fetchingMembersOperationStatus = useSelector(
    getNetworkOperationStatusFetchMicrosoftMembers
  );
  const addingMemberOperationStatus = useSelector(getNetworkOperationStatusAddMember);
  const removingMemberOperationStatus = useSelector(getNetworkOperationStatusRemoveMember);

  const { canRemoveUser, setIsAddingUser, setIsRemovingUser, setIsPanelOpen, setSelectedEmail } =
    props;

  useEffect((): any => {
    if (addingMemberOperationStatus === OperationStatus.Succeeded) {
      setIsPanelOpen(false);
    }
  }, [addingMemberOperationStatus]);

  useEffect((): any => {
    if (removingMemberOperationStatus === OperationStatus.Succeeded) {
      setIsPanelOpen(false);
    }
  }, [removingMemberOperationStatus]);

  useEffect(() => {
    const page = currentPage - 1;
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;

    setMembers(microsoftMembers.slice(startIndex, endIndex));

    return (): void => {
      setMembers([]);
    };
  }, [microsoftMembers, pageSize, currentPage]);

  const columns: IColumn[] = [
    {
      key: "displayName",
      name: "Name",
      ariaLabel: "Name",
      fieldName: "displayName",
      minWidth: 240,
      isResizable: true,
    },
    {
      key: "email",
      name: "Email",
      ariaLabel: "Email",
      fieldName: "email",
      minWidth: 240,
      isResizable: true,
    },
  ];

  const handleClick = (email: string): void => {
    if (canRemoveUser) {
      setIsAddingUser(false);
      setIsRemovingUser(true);
      setSelectedEmail(email);
      setIsPanelOpen(true);
    }
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): any => {
    const selectedMicrosoftMember = props!.item as any;

    return defaultRender!({
      ...props,
      item: selectedMicrosoftMember,
      onClick: () => handleClick(selectedMicrosoftMember.email),
    });
  };

  if (fetchingMembersOperationStatus === OperationStatus.Pending) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  if (microsoftMembers && microsoftMembers.length === 0) {
    return <p>There are no microsoft members in this DL.</p>;
  }

  const onChangeText = (ev, text): void => {
    const displayNameLookup = microsoftMembers.filter(
      (x) => x.displayName.toLowerCase().indexOf(text.toLowerCase()) > -1
    );

    const emailLookup = microsoftMembers.filter(
      (x) => x.email.toLowerCase().indexOf(text.toLowerCase()) > -1
    );

    let lookupResults = _union(displayNameLookup, emailLookup);

    if (text.length === 0) {
      lookupResults = microsoftMembers;
    }

    setMembers(lookupResults);
  };

  return (
    <>
      {/*
          Hard coding style so that this can cherry-picked into the release branch, the PR that introduced the LabelStyle
          is in a feature being developed this sprint and is not ready to be merged into the release branch.
        */}
      <Stack>
        <Text styles={{ root: { fontWeight: 600 } }}>Total Count: {microsoftMembers.length}</Text>
      </Stack>
      <Stack styles={{ root: { fontWeight: 600 } }}>
        <TextField label="Filter lookup: " onChange={onChangeText} />
      </Stack>
      <br />
      <Stack>
        <DetailsList
          items={members}
          columns={columns}
          selectionMode={SelectionMode.none}
          getKey={(item: IDistributionGroupMember): string => `${item.email}`}
          setKey="none"
          layoutMode={DetailsListLayoutMode.fixedColumns}
          isHeaderVisible={true}
          onRenderRow={onRenderRow}
          enterModalSelectionOnTouch={true}
          styles={detailListStyles}
        />
        <PaginationRow>
          <Pagination
            total={microsoftMembers.length}
            onChange={(page): void => {
              setCurrentPage(page);
            }}
            showTitle={false}
            current={currentPage}
            prevIcon={ChevronIcon("ChevronLeft")}
            nextIcon={ChevronIcon("ChevronRight")}
            pageSize={pageSize}
            showSizeChanger
          />
          <Dropdown
            options={[
              { key: 16, text: "Show 16 results" },
              { key: 32, text: "Show 32 results" },
              { key: 48, text: "Show 48 results" },
            ]}
            onChange={(_e, op): void => {
              setCurrentPage(1);
              setPageSize(op?.key as number);
            }}
            selectedKey={pageSize}
            className="pagination-dropdown"
            title="Page Size Dropdown"
          />
        </PaginationRow>
      </Stack>
    </>
  );
};

export default MicrosoftMembersList;
